import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Button } from '../../../components/ui';
import {
  buildCalendarWeeks,
  buildHalfHourSlots,
  buildWeekJumpLabel,
  findDefaultWeekKey,
  getScheduleBounds,
  normalizeScheduleWindows,
} from '../utils/votingScheduleFormatter';
import { ElectionWeekCalendarWeek } from './ElectionWeekCalendarWeek';

export function ElectionWeekCalendar({ scheduleWindows }) {
  const normalizedWindows = useMemo(
    () => normalizeScheduleWindows(scheduleWindows),
    [scheduleWindows]
  );

  const bounds = useMemo(() => getScheduleBounds(normalizedWindows), [normalizedWindows]);

  const slots = useMemo(
    () => buildHalfHourSlots(bounds.earliestTime, bounds.latestTime),
    [bounds.earliestTime, bounds.latestTime]
  );

  const weeks = useMemo(() => buildCalendarWeeks(normalizedWindows), [normalizedWindows]);

  const defaultWeekKey = useMemo(() => findDefaultWeekKey(weeks), [weeks]);

  if (!weeks.length) {
    return null;
  }

  return (
    <div className="rounded-3xl border border-stone-200 bg-stone-50">
      <div className="border-b border-stone-200 px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-semibold text-stone-900">Voting calendar</h3>
            <p className="mt-1 text-sm text-stone-600">
              Scroll through each week to compare all available voting hours at once.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {weeks.map((week) => (
              <Button
                key={week.key}
                type="button"
                variant={week.key === defaultWeekKey ? 'default' : 'secondary'}
                className="text-xs"
                onClick={() => {
                  const target = document.getElementById(week.key);
                  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {buildWeekJumpLabel(week)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-h-[72vh] overflow-y-auto">
        {weeks.map((week) => (
          <ElectionWeekCalendarWeek key={week.key} week={week} slots={slots} />
        ))}
      </div>
    </div>
  );
}

ElectionWeekCalendar.propTypes = {
  scheduleWindows: PropTypes.arrayOf(PropTypes.object),
};

ElectionWeekCalendar.defaultProps = {
  scheduleWindows: [],
};
